import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import AnDateTimePicker from '../../../components/DateTime/DateTimePicker'
import DocumentsField from '../../../helpers/DocumentsField'
import {
  DEMO_UPLOAD_MESSAGE,
  fileRecord,
  uploadDocument,
} from '../../../helpers/uploadDocument'

// Mirrors FUEL_TANK_LEVELS in server/src/models/fleetEquipment/FuelLog.js
// and the mobile new/fuel screen.
export const TANK_LEVELS = [
  { value: 'Empty', label: 'Empty' },
  { value: '1/4', label: '¼' },
  { value: '1/2', label: '½' },
  { value: '3/4', label: '¾' },
  { value: 'Full', label: 'Full' },
]

const toNumber = (value) => {
  if (value === '' || value === null || value === undefined) return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : NaN
}

// Record one refuel. `vehicle` pre-selects and locks the vehicle (Fuel tab
// on the vehicle page); otherwise the crew pick it.
const FuelLogForm = ({ vehicle: presetVehicle, onSaved, onCancel }) => {
  const { setToast } = useContext(GlobalContext)
  const [vehicleOptions, setVehicleOptions] = useState([])
  const [vehicle, setVehicle] = useState(presetVehicle ?? null)
  const [filledAt, setFilledAt] = useState(moment().toISOString())
  const [litres, setLitres] = useState('')
  const [pricePerLitre, setPricePerLitre] = useState('')
  const [totalCost, setTotalCost] = useState('')
  const [totalEdited, setTotalEdited] = useState(false)
  const [tankLevel, setTankLevel] = useState('Full')
  const [mileage, setMileage] = useState('')
  const [notes, setNotes] = useState('')
  const [receiptFiles, setReceiptFiles] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (presetVehicle) return
    API.get('/vehicleList').then((res) => {
      if (res.status === 200 && Array.isArray(res.data)) {
        setVehicleOptions(
          [...res.data].sort(
            (a, b) =>
              (a.vehicleType || '').localeCompare(b.vehicleType || '') ||
              (a.callSign || '').localeCompare(b.callSign || ''),
          ),
        )
      }
    })
  }, [presetVehicle])

  // Keep the total in step with litres × price until someone types their own.
  const calculatedTotal = useMemo(() => {
    const l = toNumber(litres)
    const p = toNumber(pricePerLitre)
    if (!l || !p || Number.isNaN(l) || Number.isNaN(p)) return ''
    return (l * p).toFixed(2)
  }, [litres, pricePerLitre])
  const shownTotal = totalEdited ? totalCost : calculatedTotal

  const save = async () => {
    setError(null)
    const l = toNumber(litres)
    const p = toNumber(pricePerLitre)
    const t = toNumber(shownTotal)
    const m = toNumber(mileage)
    if (!vehicle?._id) return setError('Select the vehicle that was refuelled.')
    if (!filledAt) return setError('Enter when the vehicle was refuelled.')
    if (moment(filledAt).isAfter(moment().add(1, 'hour'))) {
      return setError('The refuel time cannot be in the future.')
    }
    if (!l || Number.isNaN(l) || l <= 0) {
      return setError('Enter how many litres were added.')
    }
    if ([p, t, m].some((n) => Number.isNaN(n) || n < 0)) {
      return setError('Price, total and mileage must be positive numbers.')
    }

    setSaving(true)
    try {
      let receipt
      if (receiptFiles[0]) {
        const key = await uploadDocument(receiptFiles[0], 'fleetequipment/fuel')
        receipt = fileRecord(receiptFiles[0], key)
      }
      const res = await API.post(
        '/fuel-logs',
        {
          vehicle: vehicle._id,
          filledAt,
          litres: l,
          pricePerLitre: p,
          totalCost: t,
          tankLevel,
          mileage: m,
          notes: notes.trim() || undefined,
          receipt,
        },
        { skipGlobalErrorHandler: true },
      )
      if (res.status !== 200) {
        if (receipt?.key) {
          API.post('/deletes3object', { key: receipt.key }).catch(() => {})
        }
        setError(res.data?.error || 'Unable to save the fuel record. Please try again.')
        return
      }
      setToast?.({ message: `Fuel recorded for ${vehicle.callSign}` })
      onSaved?.(res.data, vehicle)
    } catch (err) {
      setError(
        err?.message === DEMO_UPLOAD_MESSAGE
          ? DEMO_UPLOAD_MESSAGE
          : 'Unable to save - the receipt may have failed to upload. Check your connection and try again.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box>
      <Grid container spacing={2}>
        {!presetVehicle && (
          <Grid item xs={12}>
            <Autocomplete
              options={vehicleOptions}
              groupBy={(option) => option.vehicleType || 'Other'}
              getOptionLabel={(option) =>
                [option.callSign, option.regPlate].filter(Boolean).join(' · ')
              }
              isOptionEqualToValue={(a, b) => a._id === b._id}
              value={vehicle}
              onChange={(e, value) => setVehicle(value)}
              renderInput={(params) => (
                <TextField {...params} label="Vehicle" required />
              )}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <AnDateTimePicker
            fullWidth
            label="Refuelled at"
            date={filledAt}
            onChange={setFilledAt}
            disableFuture
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Fuel added"
            type="number"
            value={litres}
            onChange={(e) => setLitres(e.target.value)}
            inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">litres</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Price per litre"
            type="number"
            value={pricePerLitre}
            onChange={(e) => setPricePerLitre(e.target.value)}
            helperText="Optional — leave blank for fuel-card fills"
            inputProps={{ min: 0, step: 0.001, inputMode: 'decimal' }}
            InputProps={{
              startAdornment: <InputAdornment position="start">£</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Total cost"
            type="number"
            value={shownTotal}
            onChange={(e) => {
              setTotalEdited(e.target.value !== '')
              setTotalCost(e.target.value)
            }}
            helperText={
              !totalEdited && calculatedTotal
                ? 'Calculated from litres × price'
                : 'Optional'
            }
            inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
            InputProps={{
              startAdornment: <InputAdornment position="start">£</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" fontWeight={600} mb={0.75}>
            Tank level after refuelling
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={tankLevel}
            onChange={(e, value) => value && setTankLevel(value)}
          >
            {TANK_LEVELS.map((level) => (
              <ToggleButton
                key={level.value}
                value={level.value}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {level.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Mileage"
            type="number"
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
            helperText="Optional — odometer reading"
            inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">miles</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional — fuel station, fuel card used, AdBlue added…"
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" fontWeight={600} mb={0.75}>
            Receipt
          </Typography>
          <DocumentsField
            single
            files={receiptFiles}
            onFilesChange={setReceiptFiles}
            disabled={saving}
            accept="application/pdf,image/*"
            acceptLabel="PDF or image files"
            caption="Optional — photo or PDF of the receipt"
            maxSizeMB={20}
          />
        </Grid>
      </Grid></Box>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {onCancel && (
          <Button color="inherit" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Record fuel'}
        </Button>
      </Stack>
    </Stack>
  )
}

export default FuelLogForm
